import { inject, Injectable } from '@angular/core';
import { AccountMembership, AccountMembershipResource } from '@mkp/account-membership/data-access';
import { Account, AccountDto, AccountResource, AccountType } from '@mkp/account/data-access';
import { CompanyResource } from '@mkp/company/data-access';
import {
  CompanyProfile,
  CompanyProfileBase,
  CompanyProfileDto,
  CompanyProfileLink,
} from '@mkp/company/models';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import {
  LegalEntityCheUidPostBody,
  LegalEntityDto,
  LegalEntityPostBody,
  LegalEntityResource,
} from '@mkp/legal-entity/data-access';
import { LocationResource } from '@mkp/location/data-access';
import { WalletResource } from '@mkp/shared/data-access';
import { LegalEntityState, UUID, WalletDto } from '@mkp/shared/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnboardingStep } from './onboarding-step.model';
import { LocationDto } from '@mkp/location/models';
import {
  getAccountMembershipPostBody,
  getCreateCompanyProfileBody,
  getLocationCreateBody,
  getWalletPostBody,
  handleOnboardingError,
} from './onboarding.helpers';

@Injectable({ providedIn: 'root' })
export class OnboardingApiService {
  private legalEntityResource = inject(LegalEntityResource);
  private accountResource = inject(AccountResource);
  private accountMembershipResource = inject(AccountMembershipResource);
  private walletResource = inject(WalletResource);
  private companyResource = inject(CompanyResource);
  private locationResource = inject(LocationResource);

  public createLegalEntityByCheUid(
    claimCompanyDto: LegalEntityCheUidPostBody
  ): Observable<LegalEntityDto> {
    return this.legalEntityResource
      .createByCheUid(claimCompanyDto)
      .pipe(handleOnboardingError(OnboardingStep.CreateLegalEntity));
  }

  public createLegalEntity(legalEntityPostBody: LegalEntityPostBody): Observable<LegalEntityDto> {
    return this.legalEntityResource
      .add(legalEntityPostBody)
      .pipe(handleOnboardingError(OnboardingStep.CreateLegalEntity));
  }

  public activateLegalEntity({ id, _version }: LegalEntityDto): Observable<LegalEntityDto> {
    return this.legalEntityResource
      .update(id, { state: LegalEntityState.Active, _version })
      .pipe(handleOnboardingError(OnboardingStep.ActivateLegalEntity));
  }

  public findLegalEntityForRecovery(
    legalEntityId: AccountDto['legalEntityId']
  ): Observable<LegalEntityDto> {
    return this.legalEntityResource
      .getById(legalEntityId)
      .pipe(handleOnboardingError(OnboardingStep.FindLegalEntityForRecovery));
  }

  public findInitialLegalEntity(): Observable<LegalEntityDto> {
    return this.legalEntityResource
      .getWithQuery({ params: { limit: 1 }, context: getOptionalContext() })
      .pipe(map(({ _embedded: { results } }) => results[0]))
      .pipe(handleOnboardingError(OnboardingStep.FindInitialLegalEntity));
  }

  public createAccount(
    { id, name }: LegalEntityDto,
    type: AccountType | undefined = undefined
  ): Observable<AccountDto> {
    return this.accountResource
      .add({ legalEntityId: id, name, ...(type && { type }) })
      .pipe(handleOnboardingError(OnboardingStep.CreateAccount));
  }

  public findAccountForRecovery(
    accountId: AccountDto['id'] | Account['id']
  ): Observable<AccountDto> {
    return this.accountResource
      .getById(accountId)
      .pipe(handleOnboardingError(OnboardingStep.FindAccountForRecovery));
  }

  public createMembership(account: AccountDto | Account): Observable<AccountMembership> {
    return this.accountMembershipResource
      .createAccountMembership(getAccountMembershipPostBody(account))
      .pipe(handleOnboardingError(OnboardingStep.CreateMembership));
  }

  public createWallet(accountId: AccountDto['id']): Observable<WalletDto> {
    return this.walletResource
      .create(getWalletPostBody(accountId))
      .pipe(handleOnboardingError(OnboardingStep.CreateWallet));
  }

  public createCompanyProfile(
    legalEntity: LegalEntityDto,
    companyWebsite?: CompanyProfileLink['url']
  ): Observable<CompanyProfileDto> {
    return this.companyResource
      .add(getCreateCompanyProfileBody(legalEntity, companyWebsite))
      .pipe(handleOnboardingError(OnboardingStep.CreateLegalEntity));
  }

  public createLocation(
    legalEntity: LegalEntityDto,
    company: Pick<CompanyProfileBase, 'id'>
  ): Observable<LocationDto> {
    return this.locationResource
      .add(getLocationCreateBody(legalEntity, company))
      .pipe(handleOnboardingError(OnboardingStep.CreateLocation));
  }

  /**
   * Patches the company profile to set its main location id.
   *
   * Used by the claim flow as a follow-up to {@link createLocation}, since
   * `locationId` cannot be sent at company-profile POST time (the location
   * doesn't exist yet).
   */
  public patchCompanyProfileMainLocation(
    companyProfile: Pick<CompanyProfileDto, 'id' | '_version'>,
    locationId: UUID
  ): Observable<CompanyProfile> {
    return this.companyResource
      .updateCompany({
        id: companyProfile.id,
        locationId,
        _version: companyProfile._version,
      })
      .pipe(handleOnboardingError(OnboardingStep.PatchCompanyProfileMainLocation));
  }
}
